import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useBlocker } from 'react-router-dom';
import { Button, Modal, Notice } from './index';

type Editor = {
  dirty: boolean;
  title?: string;
  description?: string;
  discardLabel?: string;
  continueLabel?: string;
  busy?: boolean;
  save?: () => Promise<boolean>;
  discard: () => void;
};
type Guard = {
  register: (id: string, editor: Editor) => () => void;
  leave: (action: () => void) => void;
};
const Context = createContext<Guard | null>(null);

export function UnsavedChangesProvider({ children }: { children: ReactNode }) {
  const editors = useRef(new Map<string, Editor>());
  const [, changed] = useState(0);
  const [action, setAction] = useState<(() => void) | null>(null);
  const [saving, setSaving] = useState(false);
  const [failed, setFailed] = useState(false);
  const active = () => [...editors.current.values()].find((e) => e.dirty);
  const editor = active();
  const blocker = useBlocker(() => !!active());
  const register = useCallback((id: string, value: Editor) => {
    editors.current.set(id, value);
    changed((n) => n + 1);
    return () => {
      editors.current.delete(id);
      changed((n) => n + 1);
    };
  }, []);
  const leave = useCallback((next: () => void) => {
    if (!active()) {
      next();
      return;
    }
    setFailed(false);
    setAction(() => next);
  }, []);
  const warn = useCallback((event: BeforeUnloadEvent) => {
    event.preventDefault();
    event.returnValue = '';
  }, []);
  useEffect(() => {
    if (!editor) return;
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [!!editor, warn]);
  const open = blocker.state === 'blocked' || !!action;
  function stay() {
    if (saving) return;
    if (blocker.state === 'blocked') blocker.reset();
    setAction(null);
    setFailed(false);
  }
  function proceed() {
    const next = action;
    // An explicit decision already handled the draft. Full-page navigation
    // can happen before React commits the clean state; do not ask twice.
    window.removeEventListener('beforeunload', warn);
    setAction(null);
    setFailed(false);
    if (blocker.state === 'blocked') blocker.proceed();
    else next?.();
  }
  return (
    <Context.Provider value={{ register, leave }}>
      {children}
      <Modal
        open={open}
        onOpenChange={(value) => {
          if (!value) stay();
        }}
        title={editor?.title || '保存当前修改？'}
        description={editor?.description || '有尚未保存的内容，请选择如何继续。'}
      >
        {failed && <Notice>保存未完成，草稿仍保留。请继续编辑并检查表单中的提示。</Notice>}
        <div className="row wrap unsaved-changes-actions">
          <Button variant="secondary" disabled={saving || editor?.busy} onClick={stay}>
            {editor?.continueLabel || '继续编辑'}
          </Button>
          <Button
            variant="ghost"
            disabled={saving || editor?.busy}
            onClick={() => {
              active()?.discard();
              proceed();
            }}
          >
            {editor?.discardLabel || '放弃修改'}
          </Button>
          {editor?.save && (
            <Button
              loading={saving}
              disabled={editor.busy}
              onClick={async () => {
                setSaving(true);
                try {
                  if (await active()?.save?.()) proceed();
                  else setFailed(true);
                } catch {
                  setFailed(true);
                } finally {
                  setSaving(false);
                }
              }}
            >
              保存并离开
            </Button>
          )}
        </div>
      </Modal>
    </Context.Provider>
  );
}

export function useUnsavedChanges(editor: Editor) {
  const guard = useContext(Context);
  const latest = useRef(editor);
  latest.current = editor;
  const id = useId();
  const register = guard?.register;
  useLayoutEffect(
    () =>
      register?.(id, {
        dirty: editor.dirty,
        busy: editor.busy,
        title: editor.title,
        description: editor.description,
        discardLabel: editor.discardLabel,
        continueLabel: editor.continueLabel,
        save: editor.save ? () => latest.current.save!() : undefined,
        discard: () => latest.current.discard(),
      }),
    [
      register,
      id,
      editor.dirty,
      editor.busy,
      !!editor.save,
      editor.title,
      editor.description,
      editor.discardLabel,
      editor.continueLabel,
    ],
  );
}
export function useConfirmLeave() {
  const guard = useContext(Context);
  return guard?.leave || ((action: () => void) => action());
}

// Keep form values in memory only. No private text is persisted in browser storage.
function formSnapshot(form: HTMLFormElement) {
  return JSON.stringify(
    Array.from(form.elements).flatMap((field) => {
      if (field instanceof HTMLInputElement) {
        if (['submit', 'button', 'file'].includes(field.type)) return [];
        return [[field.name, field.type, field.value, field.checked]];
      }
      if (field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)
        return [[field.name, field.value]];
      return [];
    }),
  );
}
export function useFormDraft({
  enabled,
  busy,
  save,
  discard,
  title,
  description,
}: {
  enabled: boolean;
  busy?: boolean;
  save?: (form: HTMLFormElement) => Promise<boolean>;
  discard: () => void;
  title?: string;
  description?: string;
}) {
  const form = useRef<HTMLFormElement | null>(null);
  const baseline = useRef('');
  const [dirty, setDirty] = useState(false);
  const changed = useCallback(() => {
    if (form.current) setDirty(formSnapshot(form.current) !== baseline.current);
  }, []);
  const ref = useCallback(
    (node: HTMLFormElement | null) => {
      form.current?.removeEventListener('change', changed);
      form.current = node;
      baseline.current = node ? formSnapshot(node) : '';
      node?.addEventListener('change', changed);
      setDirty(false);
    },
    [changed],
  );
  // Controlled selects and checkboxes may commit their values after onChange.
  useLayoutEffect(changed);
  function clean() {
    baseline.current = form.current ? formSnapshot(form.current) : '';
    setDirty(false);
  }
  useUnsavedChanges({
    dirty: enabled && dirty,
    busy,
    title,
    description,
    save: save
      ? async () => {
          if (!form.current || !form.current.reportValidity()) return false;
          const saved = await save(form.current);
          if (saved) clean();
          return saved;
        }
      : undefined,
    discard: () => {
      clean();
      discard();
    },
  });
  return { ref, onChange: changed, clean };
}
